import {
    Directive,
    ElementRef,
    HostListener,
    Input,
    OnDestroy,
    Renderer2,
} from '@angular/core';

@Directive({
  selector: '[appSmartTooltip]',
})
export class SmartTooltipDirective implements OnDestroy {
  @Input('appSmartTooltip') text: string | null = null;
  @Input() tooltipPosition: 'top' | 'bottom' | 'auto' = 'auto';
  @Input() tooltipClass = ''; // opcional: nome de classe para estilizar

  private tooltipEl?: HTMLElement;

  constructor(private host: ElementRef, private renderer: Renderer2) {}

  @HostListener('mouseenter')
  onEnter() {
    this.show();
  }

  @HostListener('mouseleave')
  onLeave() {
    this.hide();
  }

  @HostListener('window:scroll', ['$event'])
  onScroll(event: Event) {
    this.hide(); // event se precisar
  }

  @HostListener('window:resize')
  onResize() {
    this.hide(); // esconder em resize para evitar desalinhamento
  }

  private show() {
    if (!this.text) return;
    if (this.tooltipEl) return; // já aberto

    const hostRect = this.host.nativeElement.getBoundingClientRect();

    // cria elemento
    this.tooltipEl = this.renderer.createElement('div');
    if (this.tooltipClass) {
      this.renderer.addClass(this.tooltipEl, this.tooltipClass);
    }

    // estilos base (pode ajustar)
    this.renderer.setStyle(this.tooltipEl, 'position', 'fixed');
    this.renderer.setStyle(this.tooltipEl, 'z-index', '11000');
    this.renderer.setStyle(this.tooltipEl, 'padding', '6px 10px');
    this.renderer.setStyle(this.tooltipEl, 'background', '#000000');
    this.renderer.setStyle(this.tooltipEl, 'color', '#ffffff');
    this.renderer.setStyle(this.tooltipEl, 'border', '1px solid rgba(255,255,255,0.15)');
    this.renderer.setStyle(this.tooltipEl, 'border-radius', '6px');
    this.renderer.setStyle(this.tooltipEl, 'font-size', '14px');
    this.renderer.setStyle(this.tooltipEl, 'max-width', '90vw');
    this.renderer.setStyle(this.tooltipEl, 'white-space', 'normal');
    this.renderer.setStyle(this.tooltipEl, 'word-break', 'break-word');
    this.renderer.setStyle(this.tooltipEl, 'box-sizing', 'border-box');
    this.renderer.setStyle(this.tooltipEl, 'pointer-events', 'none'); // não atrapalha hover
    this.renderer.setStyle(this.tooltipEl, 'visibility', 'hidden'); // medir antes de mostrar

    // conteúdo seguro (texto simples)
    this.tooltipEl && (this.tooltipEl.textContent = String(this.text));

    // anexa no body para escapar de overflow do container
    this.renderer.appendChild(document.body, this.tooltipEl);

    const ttRect = this.tooltipEl!.getBoundingClientRect();
    const gap = 8;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // calcula left centrado e clampa para dentro da viewport
    let left = hostRect.left + (hostRect.width - ttRect.width) / 2;
    left = Math.max(8, Math.min(left, vw - ttRect.width - 8));

    // decide altura (prefer bottom)
    let top = hostRect.bottom + gap;
    const preferTop = this.tooltipPosition === 'top';
    const preferBottom = this.tooltipPosition === 'bottom';

    if (preferTop) {
      top = hostRect.top - ttRect.height - gap;
    } else if (this.tooltipPosition === 'auto') {
      // se bottom estourar, troca para top
      if (top + ttRect.height > vh - 8) {
        top = hostRect.top - ttRect.height - gap;
      }
      // se top também estourar -> forçar bottom mas clamped verticalmente
      if (top < 8) {
        top = Math.max(8, hostRect.bottom + gap);
      }
    } else {
      // preferBottom: se bottom estourar, tentar top
      if (top + ttRect.height > vh - 8) {
        const altTop = hostRect.top - ttRect.height - gap;
        if (altTop >= 8) top = altTop;
        else top = Math.max(8, vh - ttRect.height - 8);
      }
    }

    // garante que tooltip não fique fora da tela verticalmente
    top = Math.max(8, Math.min(top, vh - ttRect.height - 8));

    // aplica posição e mostra
    this.renderer.setStyle(this.tooltipEl, 'left', `${Math.round(left)}px`);
    this.renderer.setStyle(this.tooltipEl, 'top', `${Math.round(top)}px`);
    this.renderer.setStyle(this.tooltipEl, 'visibility', 'visible');
  }

  private hide() {
    if (!this.tooltipEl) return;
    try {
      this.renderer.removeChild(document.body, this.tooltipEl);
    } catch {}
    this.tooltipEl = undefined;
  }

  ngOnDestroy(): void {
    this.hide();
  }
}
